import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

export interface Article {
  id: string;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  content: string;
  image_url: string | null;
  published_at: string;
  category?: string;
  excerpt?: string;
  author?: string;
  readTime?: string;
}

export const useArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('articles_cache')
          .select('*')
          .order('published_at', { ascending: false });

        if (fetchError) throw fetchError;

        setArticles(data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des articles:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  return { articles, loading, error };
};

export const useArticleBySlug = (slug: string | undefined) => {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    const fetchArticle = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('articles_cache')
          .select('*')
          .eq('slug', slug)
          .single();

        if (fetchError) throw fetchError;

        setArticle(data);
      } catch (err) {
        console.error('Erreur lors du chargement de l\'article:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  return { article, loading, error };
};
